// ============================================
// FILE: /frontend/src/components/listings/SimilarListings.jsx
// PURPOSE: Similar listings widget
// FIXED: Better error handling and image URLs
// ============================================

import { useState, useEffect } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { discoveryAPI } from '../../api/discovery.api';
import { useNavigate } from 'react-router-dom';

export default function SimilarListings({ currentListingId, category, county }) {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const fetchSimilarListings = async () => {
      if (!currentListingId) {
        console.log('[SimilarListings] No listing ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('[SimilarListings] Fetching for listing:', currentListingId);
        setLoading(true);
        
        const response = await discoveryAPI.getSimilarListings(currentListingId, 5);
        console.log('[SimilarListings] Response received:', response);
        
        if (!isCancelled) {
          setListings(response.listings || []);
        }
      } catch (error) {
        console.error('[SimilarListings] Failed to fetch:', error);
        console.error('[SimilarListings] Error details:', error.response?.data || error.message);
        
        if (!isCancelled) {
          setListings([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchSimilarListings();

    return () => {
      isCancelled = true;
    };
  }, [currentListingId]);

  // Don't show anything while loading
  if (loading) {
    return (
      <Card>
        <CardHeader className="p-3 sm:p-4 md:p-6">
          <CardTitle className="text-base sm:text-lg md:text-xl">Similar Listings</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-6 sm:py-8 p-3 sm:p-4 md:p-6">
          <Loader2 className="w-5 h-5 sm:w-6 sm:h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Don't render if no listings
  if (listings.length === 0) {
    console.log('[SimilarListings] No similar listings found');
    return null;
  }

  return (
    <Card>
      <CardHeader className="p-3 sm:p-4 md:p-6">
        <CardTitle className="text-base sm:text-lg md:text-xl">Similar Listings</CardTitle>
      </CardHeader>

      <CardContent className="space-y-2 sm:space-y-3 p-3 sm:p-4 md:p-6">
        {listings.map((listing) => {
          // Get first image and construct full URL
          let imageUrl = Array.isArray(listing.imageUrl) && listing.imageUrl.length > 0
            ? listing.imageUrl[0]
            : listing.imageUrl;

          if (imageUrl && !imageUrl.startsWith('http')) {
            imageUrl = `${import.meta.env.VITE_BASE_URL || 'http://localhost:5000'}${imageUrl}`;
          }

          return (
            <div
              key={listing.id}
              onClick={() => navigate(`/listing/${listing.id}`)}
              className="flex gap-2 sm:gap-3 p-2 sm:p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
            >
              {/* Image */}
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded overflow-hidden bg-gray-200">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/placeholder-car.jpg';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    No Image
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-xs sm:text-sm md:text-base truncate">
                  {listing.title}
                </h4>
                <p className="text-primary font-bold text-xs sm:text-sm md:text-base mt-0.5 sm:mt-1">
                  KES {parseFloat(listing.price).toLocaleString()}
                </p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">
                  {listing.county}
                </p>
              </div>

              {/* Arrow */}
              <div className="flex items-center">
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}